import React, { Fragment, useCallback, useMemo, useRef } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { useUFOReportError } from '@atlaskit/react-ufo/report-error';
import { NavigationExperienceId } from '@atlassian/navigation-perf-utils';

import { getMessagesForLocation } from '../common/utils/get-messages-for-location';
import { AsyncIntlProvider } from '../common/utils/locale';
import { useNavBoundaryContext } from '../controllers/nav-boundary-context';
import { useMessageFlags } from '../controllers/use-message-flags';

import { ErrorBoundaryUI } from './error-boundary-ui';
import type { NavErrorBoundaryProps } from './types';

export function NavErrorBoundary(props: NavErrorBoundaryProps): React.JSX.Element {
	const { UNSAFE_wrapper } = props;

	const WrapperComponent = useMemo(
		() => (UNSAFE_wrapper && fg('navx-gsn-dnc-ownership-marker') ? UNSAFE_wrapper : Fragment),
		[UNSAFE_wrapper],
	);

	return (
		<WrapperComponent>
			<AsyncIntlProvider>
				<FlagsProvider>
					<InnerErrorBoundary {...props} />
				</FlagsProvider>
			</AsyncIntlProvider>
		</WrapperComponent>
	);
}

function InnerErrorBoundary({
	disableAnalytics = false,
	disableSentry = false,
	errorActionUI,
	errorFallbackUI,
	errorMessages,
	interactionId,
	location = 'unknown',
	onError,
	showFlags,
	silent,
	children,
	sentryConfig,
	tags,
	ufoInteractionName,
}: NavErrorBoundaryProps) {
	const { formatMessage } = useIntl();
	const { header, description, values } = useMemo(
		() => getMessagesForLocation(location),
		[location],
	);
	const flags = useMessageFlags({ showFlags });
	const reportError = useUFOReportError();

	// Use ref to store the reportError function to avoid dependency issues
	const reportErrorRef = useRef(reportError);
	reportErrorRef.current = reportError;

	const failureTitle = formatMessage(header, values);
	const failureDescription = formatMessage(description, values);

	const onErrorUIHandler = useCallback(
		(error: Error) => {
			// Show failure flag if enabled
			flags?.showFailure?.({
				title: failureTitle,
				description: failureDescription,
			});

			// Mark UFO Experience as errored if ufoInteractionName provided
			if (ufoInteractionName) {
				reportErrorRef.current({
					name: ufoInteractionName,
					errorMessage: error.message,
					errorStack: error.stack,
					errorStatusCode: error instanceof Response ? error?.status : undefined,
				});
			} else {
				// Maintain backward compatibility with interactionId until removed from props
				const interactionName = interactionId && NavigationExperienceId[interactionId].experienceId;
				if (interactionName) {
					reportErrorRef.current({
						name: interactionName,
						errorMessage: error.message,
						errorStack: error.stack,
						errorStatusCode: error instanceof Response ? error?.status : undefined,
					});
				}
			}

			onError?.(error);
		},
		[flags, failureDescription, failureTitle, interactionId, onError, ufoInteractionName],
	);

	const { handleError } = useNavBoundaryContext({
		disableAnalytics,
		disableSentry,
		interactionId,
		location,
		onError: onErrorUIHandler,
		sentryConfig,
		tags,
	});

	// When `silent` is true or `flags` are enabled, we don't want to show any UI on error
	const errorComponent =
		silent || flags ? (
			<></>
		) : (
			<ErrorBoundaryUI
				disableAnalytics={disableAnalytics}
				errorActionUI={errorActionUI}
				errorFallbackUI={errorFallbackUI}
				errorMessages={errorMessages}
				location={location}
			/>
		);

	return (
		<ErrorBoundary onError={handleError} fallback={errorComponent}>
			{children}
		</ErrorBoundary>
	);
}
